import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Building2, ListChecks, Wallet, BookOpen, UserCircle, Archive, Rocket, Users2,
  ClipboardList, FileText, ScrollText, Building, Receipt, Sparkles, Leaf, Share2, Handshake, Target, Clock, FileSpreadsheet,
  TrendingUp, UserCog,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ADMIN_LINKS = [
  { to: "/", label: "Dashboard Home", icon: LayoutDashboard, testId: "nav-dashboard-home" },
  { to: "/employees", label: "Employee Roster", icon: Users, testId: "nav-employee-roster" },
  { to: "/my-schedule", label: "My Schedule", icon: Clock, testId: "nav-my-schedule" },
  { to: "/clients", label: "Client Accounts", icon: Building2, testId: "nav-client-accounts" },
  { to: "/leads", label: "Leads", icon: Target, testId: "nav-leads" },
  { to: "/tasks", label: "Task Board", icon: ListChecks, testId: "nav-task-board" },
  { to: "/opportunities", label: "Opportunities", icon: Rocket, testId: "nav-opportunities" },
  { to: "/social-media", label: "Social Media", icon: Share2, testId: "nav-social-media" },
  { to: "/meeting-discussions", label: "Meeting Discussions", icon: Handshake, testId: "nav-meeting-discussions" },
  { to: "/templates", label: "Templates & Checklists", icon: ClipboardList, testId: "nav-templates" },
  { to: "/sops", label: "SOP Library", icon: ScrollText, testId: "nav-sop-library" },
  { to: "/company-records", label: "Company Records", icon: Building, testId: "nav-company-records" },
  { to: "/company-profile", label: "About EnviroWealth", icon: Leaf, testId: "nav-company-profile" },
  { to: "/ledger", label: "Financial Ledger", icon: Wallet, testId: "nav-financial-ledger" },
  { to: "/financial-overview", label: "Financial Overview", icon: TrendingUp, testId: "nav-financial-overview" },
  { to: "/purchase-invoices", label: "Purchase Invoices", icon: FileSpreadsheet, testId: "nav-purchase-invoices" },
  { to: "/expenses", label: "Expense Tracker", icon: Receipt, testId: "nav-expense-tracker" },
  { to: "/ca-access", label: "CA Access", icon: UserCog, testId: "nav-ca-access" },
  { to: "/knowledge", label: "Knowledge Hub", icon: BookOpen, testId: "nav-knowledge-hub" },
  { to: "/research", label: "Research Archive", icon: Archive, testId: "nav-research-archive" },
  { to: "/event-contacts", label: "Event Contacts", icon: Users2, testId: "nav-event-contacts" },
  { to: "/assistant", label: "AI Assistant", icon: Sparkles, testId: "nav-assistant" },
  { to: "/activity-logs", label: "Activity Logs", icon: FileText, testId: "nav-activity-logs" },
];

const EMPLOYEE_LINKS = [
  { to: "/", label: "Dashboard Home", icon: LayoutDashboard, testId: "nav-dashboard-home" },
  { to: "/profile", label: "My Profile", icon: UserCircle, testId: "nav-my-profile" },
  { to: "/my-schedule", label: "My Schedule", icon: Clock, testId: "nav-my-schedule" },
  { to: "/my-clients", label: "My Clients", icon: Building2, testId: "nav-my-clients" },
  { to: "/leads", label: "Leads", icon: Target, testId: "nav-leads" },
  { to: "/tasks", label: "Task Board", icon: ListChecks, testId: "nav-task-board" },
  { to: "/opportunities", label: "Opportunities", icon: Rocket, testId: "nav-opportunities" },
  { to: "/social-media", label: "Social Media", icon: Share2, testId: "nav-social-media" },
  { to: "/meeting-discussions", label: "Meeting Discussions", icon: Handshake, testId: "nav-meeting-discussions" },
  { to: "/sops", label: "SOP Library", icon: ScrollText, testId: "nav-sop-library" },
  { to: "/company-profile", label: "About EnviroWealth", icon: Leaf, testId: "nav-company-profile" },
  { to: "/ledger", label: "Financial Ledger", icon: Wallet, testId: "nav-financial-ledger" },
  { to: "/knowledge", label: "Knowledge Hub", icon: BookOpen, testId: "nav-knowledge-hub" },
  { to: "/research", label: "Research Archive", icon: Archive, testId: "nav-research-archive" },
  { to: "/assistant", label: "AI Assistant", icon: Sparkles, testId: "nav-assistant" },
];

const CA_LINKS = [
  { to: "/financial-overview", label: "Financial Overview", icon: TrendingUp, testId: "nav-financial-overview" },
  { to: "/ledger", label: "Financial Ledger", icon: Wallet, testId: "nav-financial-ledger" },
  { to: "/purchase-invoices", label: "Purchase Invoices", icon: FileSpreadsheet, testId: "nav-purchase-invoices" },
  { to: "/expenses", label: "Expense Tracker", icon: Receipt, testId: "nav-expense-tracker" },
];

export default function Sidebar() {
  const { user } = useAuth();
  const links = user?.role === "admin" ? ADMIN_LINKS : user?.role === "ca" ? CA_LINKS : EMPLOYEE_LINKS;

  return (
    <aside
      data-testid="app-sidebar"
      className="w-64 fixed inset-y-0 left-0 border-r border-[#E2E8F0] bg-white flex flex-col z-40"
    >
      <div className="h-16 flex items-center px-6 border-b border-[#E2E8F0]">
        <img
          src="https://customer-assets.emergentagent.com/job_b49b5f10-3087-4d0f-adce-b804118659f8/artifacts/bpbmqyb1_ENVIROWEALTH%20LOGO%20%282%29.jpg"
          alt="EnviroWealth"
          className="h-8 object-contain"
          data-testid="sidebar-logo"
        />
      </div>
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {links.map(({ to, label, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={testId}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200",
                isActive
                  ? "bg-[#76C121]/10 text-[#00823B] border border-[#76C121]/30"
                  : "text-[#4B5563] hover:bg-[#F8F9FA] hover:text-[#1C1C1E] border border-transparent"
              )
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-[#E2E8F0] text-[10px] text-[#4B5563] uppercase tracking-[0.2em]">
        EnviroWealth Services Pvt. Ltd.
      </div>
    </aside>
  );
}
